'use client'

import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import {
  ArrowUp,
  ArrowUpRight,
  Check,
  Copy,
  Globe,
  LoaderCircle,
  MessageCircle,
  Plus,
  RotateCcw,
  Sprout,
  Square,
  SquarePen,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'

import { useI18n } from '@/components/i18n-provider'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { API_PREFIX } from '@/lib/api-client'
import { buildChatRequest, CHAT_MAX_IMAGES, ChatStreamError, streamChat } from '@/lib/chat-stream'
import { toast } from '@/hooks/use-toast'
import { compressImageFile } from '@/lib/image-compression'
import { fetchTtsAvailable, synthesizeSpeech, TtsError } from '@/lib/tts'
import { cn } from '@/lib/utils'
import type { ChatImage, ChatMessage, ChatSource } from '@/types/agriculture-api'

const ChatMarkdown = lazy(() =>
  import('@/components/ai/chat-markdown').then(({ ChatMarkdown }) => ({ default: ChatMarkdown }))
)

// Source files beyond this are rejected before decoding; compressed output must fit the Go limit.
const MAX_SOURCE_IMAGE_BYTES = 20 << 20
const MAX_COMPRESSED_IMAGE_BYTES = 2 << 20
// Distance (px) from the bottom within which new content keeps the log pinned.
const STICK_THRESHOLD = 80
const COMPOSER_MAX_HEIGHT = 160
const AUTO_READ_STORAGE_KEY = 'chat-auto-read'

// 自动朗读只是本机偏好：存储不可用（隐私模式等）时按关闭处理。
function readAutoReadPreference() {
  try {
    return window.localStorage.getItem(AUTO_READ_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writeAutoReadPreference(enabled: boolean) {
  try {
    window.localStorage.setItem(AUTO_READ_STORAGE_KEY, enabled ? '1' : '0')
  } catch {
    // Preference simply won't persist.
  }
}

function AssistantAvatar() {
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
      <Sprout className="size-4" aria-hidden="true" />
    </div>
  )
}

const DOMAIN_PATTERN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/

// Google 搜索来源的 uri 是 vertexaisearch 跳转链接，站点域名在 title 里；URL 上下文来源则直接取 uri 主机名。
function sourceDomain(source: ChatSource) {
  const title = source.title.trim().toLowerCase()
  if (DOMAIN_PATTERN.test(title)) return title
  try {
    const host = new URL(source.uri).hostname.toLowerCase()
    if (!host.endsWith('vertexaisearch.cloud.google.com') && DOMAIN_PATTERN.test(host)) return host
  } catch {
    // Not a parseable URL; fall back to the generic icon.
  }
  return null
}

// 图标经 Go 代取（/api/favicon），浏览器不直连第三方站点；取不到时退回地球图标。
function SourceIcon({ source }: { source: ChatSource }) {
  const domain = sourceDomain(source)
  const [failed, setFailed] = useState(false)
  if (!domain || failed) return <Globe className="size-3 shrink-0" aria-hidden="true" />
  return (
    <img
      src={`${API_PREFIX}/favicon?domain=${encodeURIComponent(domain)}`}
      alt=""
      width={12}
      height={12}
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-3 shrink-0 rounded-[3px] object-contain"
    />
  )
}

function SourceList({ sources, label }: { sources: ChatSource[]; label: string }) {
  return (
    <ul aria-label={label} className="mt-2.5 flex flex-wrap gap-1.5">
      {sources.map((source) => (
        <li key={source.uri} className="min-w-0">
          <a
            href={source.uri}
            target="_blank"
            rel="noopener noreferrer"
            title={source.title}
            className="inline-flex max-w-44 items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-emerald-300 hover:text-foreground focus-visible:outline-2 focus-visible:outline-emerald-700 dark:hover:border-emerald-800"
          >
            <SourceIcon source={source} />
            <span className="truncate">{source.title}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}

type SpeechState = 'idle' | 'loading' | 'playing'

type SpeechControl = {
  state: SpeechState
  onToggle: () => void
  speakLabel: string
  stopLabel: string
}

const TURN_ACTION_CLASS =
  'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground opacity-60 transition-opacity group-hover:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-emerald-700'

function AssistantTurn({
  content,
  sources,
  streaming = false,
  copyLabel,
  copiedLabel,
  sourcesLabel,
  speech,
}: {
  content: string
  sources?: ChatSource[]
  streaming?: boolean
  copyLabel: string
  copiedLabel: string
  sourcesLabel: string
  /** 未配置语音朗读时不传，按钮不显示。 */
  speech?: SpeechControl
}) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1500)
    return () => window.clearTimeout(timer)
  }, [copied])

  async function copy() {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
    } catch {
      // Clipboard can be unavailable (insecure context, denied permission); nothing to recover.
    }
  }

  return (
    <div className="group flex gap-3">
      <AssistantAvatar />
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'pt-0.5 text-sm leading-7 break-words text-foreground/90',
            streaming &&
              "[&>:last-child]:after:ml-0.5 [&>:last-child]:after:inline-block [&>:last-child]:after:animate-pulse [&>:last-child]:after:text-emerald-600 [&>:last-child]:after:content-['▍']"
          )}
        >
          <Suspense fallback={<p className="whitespace-pre-wrap">{content}</p>}>
            <ChatMarkdown content={content} />
          </Suspense>
        </div>
        {!streaming && sources && sources.length > 0 && (
          <SourceList sources={sources} label={sourcesLabel} />
        )}
        {!streaming && (
          <div className="mt-1 -ml-1.5 flex items-center gap-0.5">
            <button
              type="button"
              onClick={copy}
              aria-label={copied ? copiedLabel : copyLabel}
              title={copied ? copiedLabel : copyLabel}
              className={TURN_ACTION_CLASS}
            >
              {copied ? (
                <Check className="size-3.5 text-emerald-600" aria-hidden="true" />
              ) : (
                <Copy className="size-3.5" aria-hidden="true" />
              )}
            </button>
            {speech && (
              <button
                type="button"
                onClick={speech.onToggle}
                aria-label={speech.state === 'idle' ? speech.speakLabel : speech.stopLabel}
                title={speech.state === 'idle' ? speech.speakLabel : speech.stopLabel}
                aria-pressed={speech.state !== 'idle'}
                className={cn(
                  TURN_ACTION_CLASS,
                  speech.state !== 'idle' && 'text-emerald-700 opacity-100 dark:text-emerald-300'
                )}
              >
                {speech.state === 'loading' ? (
                  <LoaderCircle className="size-3.5 animate-spin" aria-hidden="true" />
                ) : speech.state === 'playing' ? (
                  <Square className="size-3 fill-current" aria-hidden="true" />
                ) : (
                  <Volume2 className="size-3.5" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function imageSrc(image: ChatImage) {
  return `data:${image.mimeType};base64,${image.data}`
}

function UserTurn({ message, imageAlt }: { message: ChatMessage; imageAlt: string }) {
  const images = message.images ?? []
  return (
    <div className="flex flex-col items-end gap-1.5 pl-10">
      {images.length > 0 && (
        <div className={cn('grid gap-1.5', images.length > 1 && 'grid-cols-2')}>
          {images.map((image, index) => (
            <img
              key={index}
              src={imageSrc(image)}
              alt={imageAlt}
              className={cn(
                'rounded-2xl border border-border object-cover',
                images.length > 1 ? 'size-28' : 'max-h-60 max-w-56'
              )}
            />
          ))}
        </div>
      )}
      {message.content && (
        <div className="rounded-2xl rounded-br-md bg-emerald-700 px-3.5 py-2 text-sm leading-6 break-words whitespace-pre-wrap text-white">
          {message.content}
        </div>
      )}
    </div>
  )
}

export function ChatAssistant() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState(false)
  const [sendingMessage, setSendingMessage] = useState<ChatMessage | null>(null)
  const [streamedReply, setStreamedReply] = useState('')
  const [error, setError] = useState('')
  const [failedMessage, setFailedMessage] = useState<ChatMessage | null>(null)
  const [attachments, setAttachments] = useState<ChatImage[]>([])
  const [attaching, setAttaching] = useState(0)
  const [attachError, setAttachError] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const dragDepthRef = useRef(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const logRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const stickRef = useRef(true)
  const [scrolled, setScrolled] = useState(false)
  const [ttsAvailable, setTtsAvailable] = useState(false)
  const [autoRead, setAutoRead] = useState(readAutoReadPreference)
  const [speech, setSpeech] = useState<{ index: number; state: 'loading' | 'playing' } | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const speechAbortRef = useRef<AbortController | null>(null)

  useEffect(() => () => abortRef.current?.abort(), [])

  useEffect(() => {
    if (open) void import('@/components/ai/chat-markdown')
  }, [open])

  useEffect(() => {
    if (!open) return
    let active = true
    void fetchTtsAvailable().then((available) => {
      if (active) setTtsAvailable(available && typeof window.AudioContext === 'function')
    })
    return () => {
      active = false
    }
  }, [open])

  // Closing the panel or unmounting stops any read-aloud in progress.
  useEffect(() => {
    if (!open) stopSpeech()
  }, [open])

  useEffect(
    () => () => {
      stopSpeech()
      void audioContextRef.current?.close()
    },
    []
  )

  // Follow new content only while the reader is already near the bottom.
  useLayoutEffect(() => {
    const log = logRef.current
    if (open && log && stickRef.current) log.scrollTop = log.scrollHeight
  }, [messages, sendingMessage, streamedReply, pending, error, open])

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, COMPOSER_MAX_HEIGHT)}px`
  }, [draft, open])

  function handleLogScroll() {
    const log = logRef.current
    if (!log) return
    stickRef.current = log.scrollHeight - log.scrollTop - log.clientHeight < STICK_THRESHOLD
    setScrolled(log.scrollTop > 4)
  }

  async function submit(message: ChatMessage) {
    if (pending) return
    // Every submit path is a click or Enter, so unlock audio now for the reply to be read later.
    const autoReadContext = autoRead && ttsAvailable ? unlockAudio() : null

    const nextMessages: ChatMessage[] = [...messages, message]
    const controller = new AbortController()
    abortRef.current = controller
    stickRef.current = true
    setPending(true)
    setSendingMessage(message)
    setStreamedReply('')
    setError('')
    setFailedMessage(null)

    let partial = ''
    let sources: ChatSource[] = []
    try {
      const reply = await streamChat(
        buildChatRequest(nextMessages),
        (delta) => {
          partial += delta
          setStreamedReply(partial)
        },
        controller.signal,
        (received) => {
          sources = received
        }
      )
      if (abortRef.current !== controller) return
      setMessages([
        ...nextMessages,
        { role: 'assistant', content: reply, ...(sources.length > 0 && { sources }) },
      ])
      if (autoReadContext) {
        stopSpeech()
        void playSpeech(nextMessages.length, reply, autoReadContext)
      }
    } catch (cause) {
      // The conversation was cleared mid-stream; drop this turn entirely.
      if (abortRef.current !== controller) return
      const aborted = controller.signal.aborted
      if (partial.trim()) {
        // Keep whatever already streamed so the turn stays coherent in the history.
        setMessages([...nextMessages, { role: 'assistant', content: partial.trim() }])
      } else {
        // Hand the unsent turn back to the composer unless the user already started a new one.
        setDraft((current) => current || message.content)
        setAttachments((current) => (current.length ? current : (message.images ?? [])))
        if (!aborted) setFailedMessage(message)
      }
      if (!aborted) {
        if (cause instanceof ChatStreamError && cause.status === 503)
          setError(t('chat.notConfigured'))
        else if (cause instanceof ChatStreamError && cause.status === 429)
          setError(t('chat.rateLimited'))
        else setError(t('chat.requestFailed'))
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
        setSendingMessage(null)
        setStreamedReply('')
        setPending(false)
      }
    }
  }

  async function addImages(files: File[]) {
    if (files.length === 0) return
    const images = files.filter((file) => file.type.startsWith('image/'))
    const room = CHAT_MAX_IMAGES - attachments.length - attaching
    setAttachError(
      images.length < files.length
        ? t('chat.imageUnsupported')
        : images.length > room
          ? t('chat.imageLimit')
          : ''
    )
    await Promise.all(
      images.slice(0, Math.max(0, room)).map(async (file) => {
        if (file.size > MAX_SOURCE_IMAGE_BYTES) {
          setAttachError(t('chat.imageTooLarge'))
          return
        }
        setAttaching((count) => count + 1)
        try {
          const { base64, mimeType } = await compressImageFile(file)
          if ((base64.length * 3) / 4 > MAX_COMPRESSED_IMAGE_BYTES) {
            setAttachError(t('chat.imageTooLarge'))
            return
          }
          setAttachments((current) =>
            [...current, { mimeType, data: base64 }].slice(0, CHAT_MAX_IMAGES)
          )
        } catch {
          setAttachError(t('chat.imageUnsupported'))
        } finally {
          setAttaching((count) => count - 1)
        }
      })
    )
  }

  function removeAttachment(index: number) {
    setAttachments((current) => current.filter((_, i) => i !== index))
    setAttachError('')
  }

  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith('image/')
    )
    if (files.length === 0) return
    // Rich clipboards (e.g. office apps) carry an image rendering of copied text; keep the text.
    if (event.clipboardData.getData('text/plain')) return
    event.preventDefault()
    void addImages(files)
  }

  function hasFiles(event: DragEvent) {
    return Array.from(event.dataTransfer.types).includes('Files')
  }

  function handleDragEnter(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return
    event.preventDefault()
    dragDepthRef.current += 1
    setDragActive(true)
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDragActive(false)
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return
    event.preventDefault()
    dragDepthRef.current = 0
    setDragActive(false)
    void addImages(Array.from(event.dataTransfer.files))
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const content = draft.trim()
    if ((!content && attachments.length === 0) || pending || attaching > 0) return
    setDraft('')
    setAttachments([])
    setAttachError('')
    void submit({
      role: 'user',
      content,
      ...(attachments.length > 0 && { images: attachments }),
    })
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      formRef.current?.requestSubmit()
    }
  }

  function retry() {
    if (!failedMessage) return
    setDraft((current) => (current === failedMessage.content ? '' : current))
    setAttachments((current) => (current === failedMessage.images ? [] : current))
    void submit(failedMessage)
  }

  function stopSpeech() {
    speechAbortRef.current?.abort()
    speechAbortRef.current = null
    const source = audioSourceRef.current
    audioSourceRef.current = null
    if (source) {
      source.onended = null
      try {
        source.stop()
      } catch {
        // Already stopped.
      }
    }
    setSpeech(null)
  }

  // Must run inside a user gesture: Safari/iOS keep an AudioContext locked if it is first
  // resumed after a network round-trip. Once resumed it stays usable for later playback.
  function unlockAudio() {
    const context = audioContextRef.current ?? new AudioContext()
    audioContextRef.current = context
    void context.resume()
    return context
  }

  function toggleSpeech(index: number, content: string) {
    const sameTurn = speech?.index === index
    stopSpeech()
    if (sameTurn) return
    void playSpeech(index, content, unlockAudio())
  }

  function toggleAutoRead() {
    const next = !autoRead
    setAutoRead(next)
    writeAutoReadPreference(next)
    if (next) unlockAudio()
    else stopSpeech()
  }

  async function playSpeech(index: number, content: string, context: AudioContext) {
    const controller = new AbortController()
    speechAbortRef.current = controller
    setSpeech({ index, state: 'loading' })
    try {
      const audio = await synthesizeSpeech(content, controller.signal)
      const buffer = await context.decodeAudioData(await audio.arrayBuffer())
      if (speechAbortRef.current !== controller) return
      const source = context.createBufferSource()
      source.buffer = buffer
      source.connect(context.destination)
      source.onended = () => {
        if (audioSourceRef.current !== source) return
        audioSourceRef.current = null
        speechAbortRef.current = null
        setSpeech(null)
      }
      audioSourceRef.current = source
      source.start()
      setSpeech({ index, state: 'playing' })
    } catch (cause) {
      if (speechAbortRef.current !== controller) return
      speechAbortRef.current = null
      setSpeech(null)
      toast({
        variant: 'destructive',
        description:
          cause instanceof TtsError && cause.status === 503
            ? t('chat.speechNotConfigured')
            : t('chat.speechFailed'),
      })
    }
  }

  function clearConversation() {
    stopSpeech()
    abortRef.current?.abort()
    abortRef.current = null
    setPending(false)
    setMessages([])
    setDraft('')
    setAttachments([])
    setAttachError('')
    setSendingMessage(null)
    setStreamedReply('')
    setError('')
    setFailedMessage(null)
    setScrolled(false)
    textareaRef.current?.focus()
  }

  const suggestions = [
    t('chat.exampleQuestion'),
    t('chat.suggestionSoil'),
    t('chat.suggestionPest'),
  ]
  const isEmpty = messages.length === 0 && !pending && !error

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={t('chat.open')}
          className="fixed right-5 bottom-5 z-40 flex size-14 items-center justify-center rounded-full bg-emerald-700 text-white shadow-lg shadow-emerald-950/20 transition-transform hover:scale-105 hover:bg-emerald-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-emerald-700 motion-reduce:transition-none sm:right-7 sm:bottom-7"
        >
          <MessageCircle className="size-6" aria-hidden="true" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        hideClose
        className="w-full gap-0 p-0 sm:max-w-[460px]"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(event) => {
          if (hasFiles(event)) event.preventDefault()
        }}
        onDrop={handleDrop}
        onOpenAutoFocus={(event) => {
          // Avoid popping the on-screen keyboard on touch devices.
          if (!window.matchMedia('(pointer: fine)').matches) return
          event.preventDefault()
          textareaRef.current?.focus()
        }}
      >
        <SheetHeader
          className={cn(
            'h-14 shrink-0 flex-row items-center gap-2.5 space-y-0 border-b px-4 py-0 transition-colors',
            scrolled ? 'border-border' : 'border-transparent'
          )}
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-sm ring-1 shadow-emerald-900/20 ring-black/5">
            <Sprout className="size-4" strokeWidth={2} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <SheetTitle className="truncate text-sm leading-5 font-semibold tracking-tight">
              {t('chat.title')}
            </SheetTitle>
            <SheetDescription className="truncate text-xs leading-4 text-muted-foreground">
              {t('chat.subtitle')}
            </SheetDescription>
          </div>
          {ttsAvailable && (
            <button
              type="button"
              onClick={toggleAutoRead}
              aria-pressed={autoRead}
              aria-label={t('chat.autoRead')}
              title={autoRead ? t('chat.autoReadOn') : t('chat.autoReadOff')}
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-emerald-700',
                autoRead
                  ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:hover:bg-emerald-900'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {autoRead ? (
                <Volume2 className="size-[17px]" strokeWidth={1.75} aria-hidden="true" />
              ) : (
                <VolumeX className="size-[17px]" strokeWidth={1.75} aria-hidden="true" />
              )}
            </button>
          )}
          {(messages.length > 0 || pending) && (
            <button
              type="button"
              onClick={clearConversation}
              aria-label={t('chat.newChat')}
              title={t('chat.newChat')}
              className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-emerald-700"
            >
              <SquarePen className="size-[17px]" strokeWidth={1.75} aria-hidden="true" />
            </button>
          )}
          <SheetClose
            aria-label={t('chat.close')}
            title={t('chat.close')}
            className="-mr-1.5 flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-emerald-700"
          >
            <X className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
          </SheetClose>
        </SheetHeader>

        <div
          ref={logRef}
          onScroll={handleLogScroll}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 py-5"
          role="log"
          aria-label={t('chat.conversation')}
          aria-live="polite"
          aria-busy={pending}
        >
          {isEmpty ? (
            <div className="my-auto flex flex-col items-center px-2 pb-6 text-center">
              <div className="flex size-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                <Sprout className="size-6" aria-hidden="true" />
              </div>
              <p className="mt-4 text-base font-semibold text-foreground">{t('chat.welcome')}</p>
              <p className="mt-1.5 max-w-80 text-sm leading-6 text-muted-foreground">
                {t('chat.intro')}
              </p>
              <ul className="mt-6 flex w-full flex-col gap-2">
                {suggestions.map((suggestion) => (
                  <li key={suggestion}>
                    <button
                      type="button"
                      onClick={() => void submit({ role: 'user', content: suggestion })}
                      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-border px-3.5 py-2.5 text-left text-sm text-foreground transition-colors hover:border-emerald-300 hover:bg-emerald-50 focus-visible:outline-2 focus-visible:outline-emerald-700 dark:hover:border-emerald-800 dark:hover:bg-emerald-950/50"
                    >
                      {suggestion}
                      <ArrowUpRight
                        className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-emerald-700 dark:group-hover:text-emerald-300"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {messages.map((message, index) =>
                message.role === 'user' ? (
                  <UserTurn key={index} message={message} imageAlt={t('chat.imageAlt')} />
                ) : (
                  <AssistantTurn
                    key={index}
                    content={message.content}
                    sources={message.sources}
                    speech={
                      ttsAvailable
                        ? {
                            state: speech?.index === index ? speech.state : 'idle',
                            onToggle: () => toggleSpeech(index, message.content),
                            speakLabel: t('chat.speak'),
                            stopLabel: t('chat.stopSpeaking'),
                          }
                        : undefined
                    }
                    copyLabel={t('chat.copy')}
                    copiedLabel={t('chat.copied')}
                    sourcesLabel={t('chat.sources')}
                  />
                )
              )}
              {sendingMessage && (
                <UserTurn message={sendingMessage} imageAlt={t('chat.imageAlt')} />
              )}
              {streamedReply ? (
                <AssistantTurn
                  content={streamedReply}
                  streaming
                  copyLabel={t('chat.copy')}
                  copiedLabel={t('chat.copied')}
                  sourcesLabel={t('chat.sources')}
                />
              ) : (
                pending && (
                  <div className="flex items-center gap-3" role="status">
                    <AssistantAvatar />
                    <span className="flex gap-1" aria-hidden="true">
                      {[0, 150, 300].map((delay) => (
                        <span
                          key={delay}
                          className="size-1.5 animate-bounce rounded-full bg-emerald-600/70 motion-reduce:animate-none"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </span>
                    <span className="sr-only">{t('chat.thinking')}</span>
                  </div>
                )
              )}
              {error && (
                <div
                  className="flex items-start justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive"
                  role="alert"
                >
                  <span className="leading-6">{error}</span>
                  {failedMessage && (
                    <button
                      type="button"
                      onClick={retry}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 font-medium hover:bg-destructive/10 focus-visible:outline-2 focus-visible:outline-destructive"
                    >
                      <RotateCcw className="size-3.5" aria-hidden="true" />
                      {t('chat.retry')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {attachError && (
            <p className="px-2 pb-2 text-xs text-destructive" role="alert">
              {attachError}
            </p>
          )}
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onClick={(event) => {
              if (event.target === event.currentTarget) textareaRef.current?.focus()
            }}
            className="flex cursor-text flex-col rounded-[22px] border border-border bg-background shadow-[0_2px_12px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow] focus-within:border-foreground/20 focus-within:shadow-[0_4px_20px_rgba(0,0,0,0.08)] dark:shadow-none"
          >
            {(attachments.length > 0 || attaching > 0) && (
              <ul className="flex flex-wrap gap-2 px-3 pt-3">
                {attachments.map((image, index) => (
                  <li key={index} className="relative">
                    <img
                      src={imageSrc(image)}
                      alt={t('chat.imageAlt')}
                      className="size-16 rounded-xl border border-border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      aria-label={t('chat.removeImage')}
                      title={t('chat.removeImage')}
                      className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-foreground text-background shadow-sm ring-2 ring-background hover:bg-foreground/85 focus-visible:outline-2 focus-visible:outline-emerald-700"
                    >
                      <X className="size-3" strokeWidth={2.5} aria-hidden="true" />
                    </button>
                  </li>
                ))}
                {Array.from({ length: attaching }, (_, index) => (
                  <li
                    key={`attaching-${index}`}
                    className="flex size-16 items-center justify-center rounded-xl bg-muted"
                  >
                    <LoaderCircle
                      className="size-4 animate-spin text-muted-foreground"
                      aria-hidden="true"
                    />
                  </li>
                ))}
              </ul>
            )}
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              onPaste={handlePaste}
              placeholder={t('chat.placeholder')}
              aria-label={t('chat.placeholder')}
              rows={1}
              maxLength={4000}
              className="max-h-40 min-h-12 resize-none bg-transparent px-4 pt-3.5 pb-1 text-[15px] leading-6 text-foreground placeholder:text-muted-foreground/80 focus:outline-none"
            />
            <div className="flex items-center justify-between px-2.5 pt-1 pb-2.5">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachments.length + attaching >= CHAT_MAX_IMAGES}
                aria-label={t('chat.attachImage')}
                title={t('chat.attachImage')}
                className="flex size-9 items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-foreground/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 disabled:cursor-default disabled:text-muted-foreground disabled:hover:bg-muted"
              >
                <Plus className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                data-testid="chat-image-input"
                onChange={(event) => {
                  void addImages(Array.from(event.target.files ?? []))
                  event.target.value = ''
                }}
              />
              {pending ? (
                <button
                  type="button"
                  onClick={() => abortRef.current?.abort()}
                  aria-label={t('chat.stop')}
                  title={t('chat.stop')}
                  className="flex size-9 items-center justify-center rounded-full bg-foreground text-background transition-colors hover:bg-foreground/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
                >
                  <Square className="size-3 fill-current" aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={(!draft.trim() && attachments.length === 0) || attaching > 0}
                  aria-label={t('chat.send')}
                  title={t('chat.send')}
                  className="flex size-9 items-center justify-center rounded-full bg-emerald-700 text-white transition-colors hover:bg-emerald-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 disabled:cursor-default disabled:bg-muted disabled:text-muted-foreground/70"
                >
                  <ArrowUp className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
                </button>
              )}
            </div>
          </form>
        </div>

        {dragActive && (
          <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-2xl border-2 border-dashed border-emerald-500 bg-emerald-50/90 text-sm font-medium text-emerald-800 dark:bg-emerald-950/90 dark:text-emerald-200">
            {t('chat.dropHint')}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
